/* ============================================================
   sound.ts — tiny synthesized sound effects.

   No audio files needed: we generate short beeps and chimes with
   the browser's built-in WebAudio API. The Home-screen sound
   toggle (saved in localStorage) silences everything.
   ============================================================ */

const KEY = "sortTheTrash.sound";
let ctx: AudioContext | null = null;

export function soundEnabled(): boolean {
  return localStorage.getItem(KEY) !== "off";
}

// Browsers only allow audio after a user gesture (a click/tap),
// so main.ts calls ensureAudio() from the Play button.
export function ensureAudio(): void {
  try {
    if (!ctx) ctx = new AudioContext();
    if (ctx.state === "suspended") void ctx.resume();
  } catch (_e) {
    /* no audio support — game still works silently */
  }
}

// Play one note: frequency (Hz), start delay (s), duration (s)
function note(
  freq: number,
  when: number,
  dur: number,
  type: OscillatorType = "sine",
  vol = 0.12,
): void {
  if (!soundEnabled()) return;
  ensureAudio();
  if (!ctx) return;
  const t = ctx.currentTime + when;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t);
  gain.gain.setValueAtTime(0.0001, t);
  gain.gain.exponentialRampToValueAtTime(vol, t + 0.02);
  gain.gain.exponentialRampToValueAtTime(0.0001, t + dur);
  osc.connect(gain).connect(ctx.destination);
  osc.start(t);
  osc.stop(t + dur + 0.05);
}

/* ---- The game's sound vocabulary ---- */
export const Sound = {
  ensure: ensureAudio,
  enabled: soundEnabled,
  correct(fast: boolean): void {
    note(660, 0, 0.12, "triangle");
    note(880, 0.09, 0.16, "triangle");
    if (fast) note(1320, 0.18, 0.12, "triangle", 0.08); // extra sparkle for speed bonus
  },
  wrong(): void {
    note(220, 0, 0.25, "sawtooth", 0.1);
    note(160, 0.12, 0.3, "sawtooth", 0.1);
  },
  streak(): void {
    [523, 659, 784, 1047].forEach((f, i) => note(f, i * 0.07, 0.12, "square", 0.07));
  },
  win(): void {
    [523, 659, 784, 1047, 1319].forEach((f, i) => note(f, i * 0.12, 0.25, "triangle"));
  },
  lose(): void {
    [392, 330, 262, 196].forEach((f, i) => note(f, i * 0.15, 0.3, "sine"));
  },
};
